<template>
  <b-container ref="waterfallSearchContainer" :class="{ 'p-0': noPadding, 'pb-3': loadMoreInProgress }">
    <b-row v-if="searchBar" :no-gutters="noGutters">
      <b-col class="search-form-col">
        <form ref="form"
              name="search_form"
              method="get"
              class="d-flex align-items-center"
              @submit.prevent="find()"
        >
          <sdui-search
            v-model="search"
            class="search-form-input"
            :placeholder="searchInputTextPlaceholder"
            :loading="!!(loadMoreInProgress && length > 0 && search.length)"
            :input-label-sr-only-text="searchInputLabelSrOnlyText"
            @update="$emit('search', search)"
          />
          <slot name="filter" :find="find" />
        </form>
      </b-col>
      <div v-if="showSearchDivider" class="gradient-top" />
    </b-row>

    <slot name="link" />
    <slot name="title" />
    <slot name="selected-users" />
    <slot name="quick-actions" />

    <b-row
      ref="waterfallSearchContent"
      :no-gutters="noGutters"
      class="position-relative"
      :class="{ 'mt-3': $hasSlotContent($slots['selected-users']) }"
    >
      <!--      <b-col class="searching-container" :class="{ 'active' : loadMoreInProgress && ((searchInProgress && length > 0) || page === 1)}">-->
      <b-col class="searching-container" :class="{ 'pb-3': loadMoreInProgress }">
        <slot name="content">
          <SList :items-count="items ? length : 0"
                 :title="title"
                 :link="link"
                 @action-click="$emit('link')"
          >
            <slot />
          </SList>
        </slot>
        <sdui-skeletons
          v-if="loadMoreInProgress && length === 0 && !hasCachedResults && skeletonType"
          :type="skeletonType"
          :skeletons="skeletonLength"
        />

        <!-- if there is a skeletonType the loading spinner should only be displayed when loading more entries-->
        <!-- else show loading spinner-->
        <sdui-loader v-if="loadMoreInProgress && (length !== 0 || !skeletonType)" class="p-3" />
        <div v-else-if="loadMoreFailed" :class="length === 0 ? 'py-6' : 'py-2'">
          <sdui-loading-problem @refetch="retryFetch" />
        </div>
        <div v-observe-visibility="(visible, entry) => visibilityChanged(visible, entry)" class="waterfall-visible-observer" />
      </b-col>
    </b-row>

    <slot v-if="noResults && !noSearchResults" name="noResults">
      <b-row data-cy="waterfall-search-no-results" :no-gutters="noGutters">
        <b-col class="text-center"
               data-test="no-result-wrapper"
               :class="noResultsClasses"
        >
          <small class="mb-3 wall-notification" data-test="no-result-text">{{ $t(noResultText) }}</small>
          <slot name="no-result-actions" />
        </b-col>
      </b-row>
    </slot>

    <!--For having the ability to give out a different message when there are no results after searching-->
    <slot v-if="noResults && noSearchResults" name="noSearchResults">
      <b-row :no-gutters="noGutters" data-test="no-search-results-slot">
        <b-col class="text-center" :class="noResultsClasses">
          <small class="mb-3 wall-notification" data-test="no-result-text">{{ $t(noResultText) }}</small>
          <slot name="no-result-actions" />
        </b-col>
      </b-row>
    </slot>
  </b-container>
</template>

<script>
// eslint-disable-next-line import/extensions
import lDebounce from 'lodash/debounce'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import SduiLoadingProblem from 'src/components/miscellaneous/ui/common/SduiLoadingProblem.vue'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import SduiSearch from '../forms/SduiSearch.vue'
import SduiSkeletons from 'src/components/miscellaneous/ui/SduiSkeletons.vue'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  components: { SduiSkeletons, SduiSearch, SduiLoadingProblem, SduiLoader },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },

    url: {
      type: String,
      default: '',
    },

    title: {
      type: String,
      default: '',
    },

    searchInputText: {
      type: String,
      default: null,
    },

    searchInputLabelSrOnlyText: {
      type: String,
      default: '',
    },

    link: {
      type: String,
      default: '',
    },

    itemsLength: {
      type: Number,
      default: -1,
    },

    pageSize: {
      type: Number,
      default: 0,
    },

    searchBar: {
      type: Boolean,
      default: true,
    },

    model: {
      type: [Array, Object, Function],
      default: null,
    },

    customFetchMethod: {
      type: Function,
      default: null,
    },

    clearSearch: {
      type: Boolean,
      default: false,
    },

    autoFetchWithInterval: {
      type: Number,
      default: 0,
    },

    /**
     * deprecated
     * use $refs.someRef.forceRefetchItems() instead
     * **/
    forceRefetch: {
      type: [String, Number, Boolean, Array, Object, Function],
      default: null,
    },

    defaultSearch: {
      type: String,
      default: '',
    },

    hasCachedResults: {
      type: Boolean,
      default: false,
    },

    noPadding: {
      type: Boolean,
      default: false,
    },

    skeletonType: {
      type: String,
      default: null,
    },

    skeletonLength: {
      type: Number,
      default: 6,
    },

    showSearchDivider: {
      type: Boolean,
      default: false,
    },

    noGutters: {
      type: Boolean,
      default: false,
    },

    noResultText: {
      type: String,
      default: 'ui.waterfall_search.no_results',
    },

    noResultsClasses: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    items: [],
    blockLoadMore: true,
    loadMoreInProgress: false,
    searchInProgress: true,
    loadMoreFailed: false,
    initialFetchFailed: false, // to keep error message while refetching data on background
    page: 1,
    timer: null,
    search: '',
  }),

  computed: {
    searchInputTextPlaceholder() {
      return this.searchInputText || this.$t('global.search')
    },

    length() {
      if (this.itemsLength !== -1) return this.itemsLength
      return this.items ? this.items.length : 0
    },

    noResults() {
      return !this.loadMoreInProgress && this.length === 0 && !this.loadMoreFailed
    },

    noSearchResults() {
      return this.searchBar && this.search && this.noResults
    },
  },

  emits: ['update:modelValue', 'search', 'loading', 'retryFetch', 'link', 'isExhausted'],

  methods: {
    /** public functions **/

    // when items are changed outside the component
    // todo: this can be replaced with a watcher on value (but complicated and a bit risky)
    updateItems(items) {
      this.items = items
    },

    async forceRefetchItems() {
      await this.fetchItems(true)
    },

    /** privat functions **/

    find() {
      this.fetchItems(true)
    },

    fetchItems: waitFor('fetch-waterfall-items', async function (removeOldData, intervalCall = false) {
      // if call is from interval, it should be processed anyways
      if (!intervalCall && this.blockLoadMore && !removeOldData && !this.loadMoreFailed) return

      this.initValues(removeOldData, intervalCall)
      const result = await this.processFetch(removeOldData, intervalCall)
      this.resetValues(result)
      if (!result?.data) return
      if (intervalCall) {
        this.items = this.processIntervalData(result)
        return
      }
      const { data } = result

      const newData = this.model ? data.map((el) => new this.model(el)) : data
      this.items = removeOldData ? newData : [...this.items, ...newData]
    }),

    debounceSearch: lDebounce(function () {
      this.fetchItems(true)
    }, 500),

    retryFetch() {
      this.$emit('retryFetch')
      const removeOldData = this.page === 1
      this.fetchItems(removeOldData)
    },

    retryFetchWithDelay() {
      this.loadMoreInProgress = true
      const self = this
      setTimeout(() => {
        self.loadMoreInProgress = false
        self.retryFetch()
      }, 1000)
    },

    initValues(removeOldData, intervalCall) {
      this.loadMoreInProgress = !intervalCall
      this.blockLoadMore = true
      this.searchInProgress = removeOldData
      this.initialFetchFailed = this.loadMoreFailed
      this.loadMoreFailed = false
      this.page = this.calculatePage(removeOldData, intervalCall)
      if (removeOldData) {
        this.$emit('isExhausted', false)
      }
    },

    processIntervalData(result) {
      const { data } = result
      // If fetched data is the same as existing, stop execution
      if (data.length === this.items.length) return this.items

      // Add new items to the list
      const newItems = data.filter((s) => !this.items.find((p) => p.id === s.id)) || []

      const castedNewItems = this.model ? newItems.map((el) => new this.model(el)) : newItems
      return [...this.items, ...castedNewItems]
    },

    async processFetch(removeOldData, intervalCall) {
      try {
        return this.customFetchMethod
          ? await this.customFetchMethod(this.page, this.search)
          : await this.$axios.get(`${this.url}page=${this.page}&search=${this.search}`)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        this.loadMoreFailed = true
        if (intervalCall) return
        this.page = removeOldData ? this.page - 1 : 1 // set page back to previous value if it's not interval
      } finally {
        this.initialFetchFailed = false
      }
    },

    calculatePage(removeOldData, intervalCall) {
      // if call is from interval, page should not be changed, request simply should be repeated
      if (intervalCall) return this.page

      return removeOldData ? 1 : this.page + 1
    },

    resetValues(result) {
      const meta = result?.meta
      this.blockLoadMore = meta?.current_page
        ? meta.current_page >= meta.last_page
        // improvement: instead of "<=" we should use "<", then the pageSize wouldn't need to be "actually size -1"
        : (!result?.data || result.data.length <= this.pageSize)
      this.loadMoreInProgress = false
      if (result?.data) {
        this.$emit('isExhausted', this.blockLoadMore)
      }
    },

    async visibilityChanged(visible) {
      // block fetch
      if (this.blockFetch(visible)) return
      await this.fetchItems(false)
    },

    blockFetch(visible) {
      // block the next fetch if observe element isn't visible or there is nothing to fetch
      return !visible || this.blockLoadMore
    },
  },

  watch: {
    search() {
      this.debounceSearch(true)
    },

    items: {
      handler: function () {
        this.$emit('update:modelValue', this.items)
      },

      deep: true,
    },

    url() {
      this.fetchItems(true)
    },

    clearSearch() {
      this.search = ''
    },

    loadMoreInProgress(bool) {
      this.$emit('loading', bool)
    },

    forceRefetch() {
      this.fetchItems(true)
    },
  },

  async created() {
    this.search = this.defaultSearch
    await this.$nextTick()
  },

  async mounted() {
    await this.$nextTick()
    this.items = this.modelValue || []
    await this.fetchItems(true)

    if (!this.autoFetchWithInterval) return

    this.timer = setInterval(() => {
      const removeOldData = false
      const intervalCall = true
      this.fetchItems(removeOldData, intervalCall)
    }, this.autoFetchWithInterval)
  },

  unmounted() {
    if (!this.timer) return
    clearInterval(this.timer)
  },
}
</script>

<style scoped lang="scss">
.search-form-input {
  flex: 1;
}

.wall-notification {
  color: $color-gray-300;
  display: block;
  white-space: pre-line;
}

.searching {
  position: absolute;
  width: 100%;
  z-index: 9999;
  left: 0;
  top: -18px;
}

.searching-container {
  transition: 0.2s;

  &.active {
    padding-top: 50px !important;
  }
}

.refetch-btn {
  max-width: 250px;
  margin: 0 auto;
}

.with-min-height {
  min-height: 90vh;
}

.update-problem {
  box-shadow: 0px 4px 12px rgba($color-black-rgb, 0.12);
  border-radius: 8px;
  min-height: 64px;
  padding-left: 12px;
  margin: 16px 0;
}

@media only screen and (max-device-width: 334px) {
  .update-problem {
    min-height: 60px;
    small {
      font-size: pxtorem(12) !important;
      margin-right: 4px !important;
    }
    .btn {
      font-size: pxtorem(14) !important;
    }
  }
}

.waterfall-visible-observer {
  height: 5px;
}
</style>
